<template>
    <div class="page">

        <!-- Top bar -->
        <div class="topbar">
            <div class="brand">
                <svg viewBox="0 0 1118 1063" aria-hidden="true"><g fill-rule="nonzero"><polygon fill="#DD6F48" points="732.5 306.8 626.1 306.8 626.1 198.2 489.3 198.2 489.3 306.8 382.9 306.8 382.9 439.3 489.3 439.3 489.3 547.9 626.1 547.9 626.1 439.3 732.5 439.3"></polygon><rect fill="#4E4E4E" x="489.8" y="0.5" width="135.3" height="163.8"></rect><path fill="#4E4E4E" d="M935.9,794.9 L947.5,790 C1018.6,759.7 1103.7,695.2 1105.2,558.7 L1105.2,547.9 C1103.9,396.8 997.5,306.6 820.3,306.6 L766.3,306.6 L766.3,306.8 L766.3,439.1 L821.5,439.1 C910,439.1 962.9,484 962.9,558.9 C962.9,633.8 908.1,678.5 821.5,678.5 L625,678.5 L625,581.8 L489.8,581.8 L489.8,1062.5 L625,1062.5 L625,811 L782.7,811 L946.7,1062.5 L1118,1062.5 L935.9,794.9 Z"></path><polygon fill="#4E4E4E" points="349 306.8 349 306.6 154.5 306.6 154.5 0.5 0.2 0.5 0.2 756.4 154.5 756.4 154.5 439.1 349 439.1"></polygon></g></svg>
                <span class="brand-name">Healthy&nbsp;Roster</span>
            </div>
            <span class="topbar-tag">Sway Support</span>
        </div>

        <!-- Card -->
        <div class="card">

            <!-- ===== FORM ===== -->
            <div v-if="!submitted">
                <!-- Header -->
                <div class="card-header">
                    <div class="eyebrow">Support Escalation</div>
                    <h1>Report a Sway testing issue</h1>
                    <p>Answer a few questions so we can best address your request. Fields marked <span class="req">*</span> are required.</p>
                </div>

                <!-- Step indicator -->
                <div class="stepbar">
                    <div class="stepbar-row">
                        <span class="step-label">Step {{ step + 1 }} · {{ stepName }}</span>
                        <span class="step-counter">{{ step + 1 }} / {{ totalSteps }}</span>
                    </div>
                    <div class="progress-track">
                        <div class="progress-fill" :style="{ width: progressPct + '%' }"></div>
                    </div>
                </div>

                <!-- Steps -->
                <div class="steps">

                    <!-- STEP 0 · REASON -->
                    <div v-show="step === 0" class="step active">
                        <div class="q-title">Reason for ticket <span class="req">*</span></div>
                        <div class="q-hint">What is happening?</div>
                        <div class="options column">
                            <button v-for="opt in REASONS" :key="opt.value" type="button" class="option"
                                    :class="{ selected: ans.reason === opt.value }"
                                    @click="pick('reason', opt.value)">
                                <span class="radio"></span><span>{{ opt.label }}</span>
                            </button>
                        </div>
                        <div v-if="showErrors && errors.reason" class="field-error">Select a reason to continue.</div>

                        <!-- Follow-up: Error entering code -->
                        <div v-if="ans.reason === V_REASON_ERROR_CODE" class="followup">
                            <div class="followup-label">Which error message did you see? <span class="req">*</span></div>
                            <select class="select" v-model="ans.errorMessage" :class="{ invalid: showErrors && errors.errorMessage }">
                                <option value="">Select the error message shown…</option>
                                <option v-for="opt in ERR_CODES" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                            </select>
                            <div v-if="showErrors && errors.errorMessage" class="field-error">Select the error message you saw.</div>
                            <div v-if="ans.errorMessage === V_ERR_OTHER" style="margin-top:12px">
                                <div class="followup-label">Describe the error <span class="req">*</span></div>
                                <textarea class="textarea" v-model="ans.errorMessageOther" rows="2" maxlength="255"
                                          placeholder="Type the exact error message or describe what you saw"
                                          :class="{ invalid: showErrors && errors.errorMessageOther }"></textarea>
                                <div class="hint-small">Max 255 characters.</div>
                                <div v-if="showErrors && errors.errorMessageOther" class="field-error">Describe the error message.</div>
                            </div>
                        </div>

                        <!-- Follow-up: Unable to progress -->
                        <div v-if="ans.reason === V_REASON_UNABLE" class="followup stack">
                            <div>
                                <div class="followup-label">Which part of the test? <span class="req">*</span></div>
                                <input class="input" type="text" v-model="ans.whichPart"
                                       placeholder="e.g. Balance, Reaction Time, Impulse Control"
                                       :class="{ invalid: showErrors && errors.whichPart }">
                                <div v-if="showErrors && errors.whichPart" class="field-error">Tell us which part of the test.</div>
                            </div>
                            <div>
                                <div class="followup-label">Affected Profile ID <span class="req">*</span></div>
                                <input class="input" type="text" v-model="ans.affectedProfileId"
                                       placeholder="Profile ID for the affected patient"
                                       :class="{ invalid: showErrors && errors.affectedProfileId }">
                                <div v-if="showErrors && errors.affectedProfileId" class="field-error">Enter the affected Profile ID.</div>
                            </div>
                        </div>

                        <!-- Follow-up: Can't see results -->
                        <div v-if="ans.reason === V_REASON_NO_RESULTS" class="followup">
                            <div class="followup-label">Profile ID <span class="optional">(optional)</span></div>
                            <input class="input" type="text" v-model="ans.resultsProfileId" placeholder="Profile ID, if known">
                        </div>
                    </div>

                    <!-- STEP 1 · TESTING TYPE -->
                    <div v-show="step === 1" class="step active">
                        <div class="q-title">Testing type <span class="req">*</span></div>
                        <div class="q-hint">What kind of test is this?</div>
                        <div class="options">
                            <button v-for="opt in TYPES" :key="opt.value" type="button" class="option"
                                    :class="{ selected: ans.testingType === opt.value }"
                                    @click="pick('testingType', opt.value)">
                                <span class="radio"></span><span>{{ opt.label }}</span>
                            </button>
                        </div>
                        <div v-if="showErrors && errors.testingType" class="field-error">Select a testing type.</div>
                    </div>

                    <!-- STEP 2 · WHEN -->
                    <div v-show="step === 2" class="step active">
                        <div class="q-title">When are you testing? <span class="req">*</span></div>
                        <div class="q-hint">Timing helps us prioritize.</div>
                        <div class="options">
                            <button v-for="opt in WHEN" :key="opt.value" type="button" class="option"
                                    :class="{ selected: ans.when === opt.value }"
                                    @click="pick('when', opt.value)">
                                <span class="radio"></span><span>{{ opt.label }}</span>
                            </button>
                        </div>
                        <div v-if="showErrors && errors.when" class="field-error">Select when you are testing.</div>
                    </div>

                    <!-- STEP 3 · PATIENTS TESTING -->
                    <div v-show="step === 3" class="step active">
                        <div class="q-title">Patients testing today <span class="req">*</span></div>
                        <div class="q-hint">How many patients are scheduled to test?</div>
                        <div class="options">
                            <button v-for="opt in COUNTS" :key="opt.value" type="button" class="option"
                                    :class="{ selected: ans.patientsTesting === opt.value }"
                                    @click="pick('patientsTesting', opt.value)">
                                <span class="radio"></span><span>{{ opt.label }}</span>
                            </button>
                        </div>
                        <div v-if="showErrors && errors.patientsTesting" class="field-error">Select a range.</div>
                    </div>

                    <!-- STEP 4 · PATIENTS AFFECTED -->
                    <div v-show="step === 4" class="step active">
                        <div class="q-title">Patients experiencing the issue <span class="req">*</span></div>
                        <div class="q-hint">How many patients are affected?</div>
                        <div class="options">
                            <button v-for="opt in COUNTS" :key="opt.value" type="button" class="option"
                                    :class="{ selected: ans.patientsAffected === opt.value }"
                                    @click="pick('patientsAffected', opt.value)">
                                <span class="radio"></span><span>{{ opt.label }}</span>
                            </button>
                        </div>
                        <div v-if="showErrors && errors.patientsAffected" class="field-error">Select a range.</div>
                    </div>

                    <!-- STEP 5 · CONTACT -->
                    <div v-show="step === 5" class="step active">
                        <div class="q-title">Your contact information <span class="req">*</span></div>
                        <div class="q-hint">Please specifically provide the email used to sign into your Sway organization.</div>
                        <div class="contact-grid">
                            <div>
                                <div class="sub-label">Name <span class="req">*</span></div>
                                <input class="input" type="text" v-model="contact.name" placeholder="Full name"
                                       :class="{ invalid: showErrors && errors.name }">
                                <div v-if="showErrors && errors.name" class="field-error">Enter your name.</div>
                            </div>
                            <!--div>
                                <div class="sub-label">Account <span class="req">*</span></div>
                                <input class="input" type="text" v-model="contact.account" placeholder="Organization / account name"
                                       :class="{ invalid: showErrors && errors.account }">
                                <div v-if="showErrors && errors.account" class="field-error">Enter your account.</div>
                            </div-->
                            <div>
                                <div class="sub-label">Email <span class="req">*</span></div>
                                <input class="input" type="email" v-model="contact.email" placeholder="name@organization.com"
                                       :class="{ invalid: showErrors && errors.email }">
                                <div v-if="showErrors && errors.email" class="field-error">{{ emailErrorText }}</div>
                            </div>
                            <div>
                                <div class="sub-label">Phone <span class="req">*</span></div>
                                <input class="input" type="tel" v-model="contact.phone" placeholder="(555) 555-5555"
                                       :class="{ invalid: showErrors && errors.phone }">
                                <div v-if="showErrors && errors.phone" class="field-error">Enter a phone number.</div>
                            </div>
                        </div>
                    </div>

                </div>

                <!-- Footer -->
                <div class="card-footer">
                    <button type="button" class="btn-back" @click="onBack" :style="{ visibility: step === 0 ? 'hidden' : 'visible' }">Back</button>
                    <button type="button" class="btn-primary" @click="onPrimary" :disabled="submitting">
                        {{ submitting ? 'Submitting…' : (step < totalSteps - 1 ? 'Continue' : 'Submit request') }}
                    </button>
                </div>
            </div>

            <!-- ===== CONFIRMATION ===== -->
            <div v-else class="confirm">
                <div class="confirm-head">
                    <div class="confirm-badge">
                        <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#fff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"></path></svg>
                    </div>
                    <h2>Support Request Received</h2>
                </div>

                <div v-if="isSev1">
                    <p>Based on your responses, your ticket has been flagged as high priority and will be reviewed by our Customer Success team.</p>
                    <p>If the criteria are met, you can expect an expedited response — within 20 minutes during office hours (9 AM–5 PM EST, Mon–Fri). Tickets submitted outside office hours or on weekends will be reviewed first thing the next business day.</p>
                    <p>Please note that priority status may be adjusted if the details of your request don't meet escalation criteria.</p>
                    <p>In the meantime, you may find our <a href="https://healthyroster.helpjuice.com/en_US/general-product-understanding/sway-tips-for-a-successful-test-session" target="_blank" rel="noopener" style="font-weight:500;text-decoration:underline">Tips for a Successful Test Session</a> helpful.</p>
                </div>
                <div v-else>
                    <p>Thanks for contacting Healthy Roster's customer support team — we look forward to helping you!</p>
                    <p>Our average response time is typically 3 – 4 hours, but during peak seasons it may take up to 1 – 2 business days for a team member to respond.</p>
                    <p>In the meantime, you may find our <a href="https://healthyroster.helpjuice.com/en_US/general-product-understanding/sway-tips-for-a-successful-test-session" target="_blank" rel="noopener" style="font-weight:500;text-decoration:underline">Tips for a Successful Test Session</a> helpful.</p>
                </div>

                <button type="button" class="btn-secondary" @click="onReset">Submit another request</button>
            </div>

        </div>

        <div class="footer-note">Healthy Roster · Sway support portal</div>

    </div>
</template>

<script>


// Well-known wire values referenced by branching logic. Extracted so the checks
// aren't magic strings — one edit site if the SF picklist ever renames.
const V_REASON_ERROR_CODE    = 'Error entering code';
const V_REASON_UNABLE        = 'Unable to progress through test';
const V_REASON_NO_RESULTS    = "Can't see results";
const V_ERR_OTHER            = 'Other';
const V_TESTING_BASELINE     = 'Baseline';
const V_WHEN_NOW             = 'Now';
const V_WHEN_NEXT_2H         = 'In 2 hours';
const V_WHEN_TODAY_2H_PLUS   = 'Today';
const V_COUNT_MEDIUM         = '5-10';
const V_COUNT_HIGH           = '11+';

// ---- Option data (label → wire value mapping) ----
const REASONS = [
    { label: 'Recovery Test', value: 'Recovery Test' },
    { label: 'Error occurred while entering testing code', value: V_REASON_ERROR_CODE },
    { label: 'Unable to progress through test', value: V_REASON_UNABLE },
    { label: "Can't see results", value: V_REASON_NO_RESULTS },
];
const ERR_CODES = [
    { label: 'This device is not supported for testing. Please use a different device or contact your test administrator for support.',
        value: 'This device is not supported for testing. Please use a different device or contact your test administrator for support.' },
    { label: 'This is an invalid or expired code. Please contact your Administrator to ensure you have an active code.',
        value: 'This is an invalid or expired code. Please contact your Administrator to ensure you have an active code.' },
    { label: 'Invalid Code. Please contact your administrator or Sway Support.',
        value: 'Invalid Code. Please contact your administrator or Sway Support.' },
    { label: 'You are not connected to the Internet. Please try again.',
        value: 'You are not connected to the Internet. Please try again.' },
    { label: 'Please log out to use this Sway Test Code. If you have any questions, please contact your administrator or Sway Support.',
        value: 'Please log out to use this Sway Test Code. If you have any questions, please contact your administrator or Sway Support.' },
    { label: 'This code is no longer valid. Please contact your Administrator for assistance.',
        value: 'This code is no longer valid. Please contact your Administrator for assistance.' },
    { label: 'An error occured while loading your code. Please try again and contact Sway if the issue persists.',
        value: 'An error occured while loading your code. Please try again and contact Sway if the issue persists.' },
    { label: "Oops! That code didn't work. Please try again.",
        value: "Oops! That code didn't work. Please try again." },
    { label: 'An unexpected error occurred. Please try again. If the issue persists, please contact Sway support.',
        value: 'An unexpected error occurred. Please try again. If the issue persists, please contact Sway support.' },
    { label: 'Not Sure', value: 'Not Sure' },
    { label: 'Other',    value: V_ERR_OTHER },
];
const TYPES = [
    { label: 'Baseline',    value: V_TESTING_BASELINE },
    { label: 'Post-Injury', value: 'Post-Injury' },
];
const WHEN = [
    { label: 'Now',                     value: V_WHEN_NOW },
    { label: 'Within the next 2 Hours', value: V_WHEN_NEXT_2H },
    { label: 'Today (>2 Hours)',        value: V_WHEN_TODAY_2H_PLUS },
    { label: 'Future',                  value: 'Future' },
    { label: 'Completed',               value: 'Completed' },
];
const COUNTS = [
    { label: '1-4',  value: '1-4' },
    { label: '5-10', value: V_COUNT_MEDIUM },
    { label: '11+',  value: V_COUNT_HIGH },
];

const STEP_NAMES = ['Reason', 'Testing type', 'Timing', 'Patients testing', 'Patients affected', 'Contact info'];
const TOTAL_STEPS = STEP_NAMES.length;

// API host resolves at build time from the mode-appropriate .env file:
//   `vue-cli-service serve`   → .env.development  → devapi host
//   `vue-cli-service build`   → .env.production   → prod host
//   `vue-cli-service build --mode staging` → .env.staging (if you add one)
// Anything prefixed with VUE_APP_ gets substituted into the bundle by webpack.
// Fallback keeps the code safe if someone builds without a .env file present.
const UPLOAD_TICKETS_URL =
        (process.env.VUE_APP_API_BASE_URL || 'https://coreapi.swaymedical.com')
        + '/api/Salesforce/UploadTickets';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default {
    name: 'SupportEscalation',
    data() {
        return {
            // Constants surfaced to template
            REASONS, ERR_CODES, TYPES, WHEN, COUNTS,
            V_REASON_ERROR_CODE, V_REASON_UNABLE, V_REASON_NO_RESULTS, V_ERR_OTHER,

            // Wizard state
            step: 0,
            showErrors: false,
            submitted: false,
            submitting: false,

            // Form state
            ans: {
                reason: '',
                errorMessage: '',
                errorMessageOther: '',
                whichPart: '',
                affectedProfileId: '',
                resultsProfileId: '',
                testingType: '',
                when: '',
                patientsTesting: '',
                patientsAffected: '',
            },
            //contact: { name: '', account: '', email: '', phone: '' },
            contact: { name: '', email: '', phone: '' },

            totalSteps: TOTAL_STEPS,
        };
    },
    computed: {
        stepName() { return STEP_NAMES[this.step]; },
        progressPct() { return ((this.step + 1) / this.totalSteps) * 100; },

        errors() {
            const a = this.ans, c = this.contact, e = {};
            if (!a.reason) e.reason = true;
            if (a.reason === V_REASON_ERROR_CODE) {
                if (!a.errorMessage.trim()) e.errorMessage = true;
                if (a.errorMessage === V_ERR_OTHER && !a.errorMessageOther.trim()) e.errorMessageOther = true;
            }
            if (a.reason === V_REASON_UNABLE) {
                if (!a.whichPart.trim()) e.whichPart = true;
                if (!a.affectedProfileId.trim()) e.affectedProfileId = true;
            }
            if (!a.testingType) e.testingType = true;
            if (!a.when) e.when = true;
            if (!a.patientsTesting) e.patientsTesting = true;
            if (!a.patientsAffected) e.patientsAffected = true;
            if (!c.name.trim()) e.name = true;
            //if (!c.account.trim()) e.account = true;
            if (!c.email.trim() || !EMAIL_RE.test(c.email.trim())) e.email = true;
            if (!c.phone.trim()) e.phone = true;
            return e;
        },
        emailErrorText() {
            return (this.contact.email.trim() && !EMAIL_RE.test(this.contact.email.trim()))
                ? 'Enter a valid email address.'
                : 'Email is required.';
        },
        signalCount() {
            const a = this.ans;
            return [
                [V_REASON_ERROR_CODE, V_REASON_UNABLE, V_REASON_NO_RESULTS].includes(a.reason),
                a.testingType === V_TESTING_BASELINE,
                [V_WHEN_NOW, V_WHEN_NEXT_2H, V_WHEN_TODAY_2H_PLUS].includes(a.when),
                [V_COUNT_MEDIUM, V_COUNT_HIGH].includes(a.patientsTesting),
                [V_COUNT_MEDIUM, V_COUNT_HIGH].includes(a.patientsAffected),
            ].filter(Boolean).length;
        },
        severity() {
            const w = this.ans.when;
            return (this.signalCount >= 2 && (w === V_WHEN_NOW || w === V_WHEN_NEXT_2H))
                ? 'Sway Sev-1'
                : 'Standard';
        },
        isSev1() { return this.severity === 'Sway Sev-1'; },
    },
    methods: {
        pick(group, value) {
            if (group === 'reason') {
                // Reset all reason follow-ups when reason changes.
                this.ans.reason = value;
                this.ans.errorMessage = '';
                this.ans.errorMessageOther = '';
                this.ans.whichPart = '';
                this.ans.affectedProfileId = '';
                this.ans.resultsProfileId = '';
            } else {
                this.ans[group] = value;
            }
        },
        stepValid(step) {
            const stepKeys = [
                ['reason', 'errorMessage', 'errorMessageOther', 'whichPart', 'affectedProfileId'],
                ['testingType'],
                ['when'],
                ['patientsTesting'],
                ['patientsAffected'],
                //['name', 'account', 'email', 'phone'],
                ['name', 'email', 'phone'],
            ];
            const e = this.errors;
            return !stepKeys[step].some(k => e[k]);
        },
        onBack() {
            this.step = Math.max(0, this.step - 1);
            this.showErrors = false;
        },
        async onPrimary() {
            
            if (this.step < this.totalSteps - 1) {
                if (this.stepValid(this.step)) {
                    this.step++;
                    this.showErrors = false;
                } else {
                    this.showErrors = true;
                }
                return;
            }
            // Final step — submit if fully valid.
            if (this.stepValid(this.totalSteps - 1) && Object.keys(this.errors).length === 0) {
                await this.submit();
            } else {
                this.showErrors = true;
            }
        },
        async submit() {
            const payload = {
                reason:            this.ans.reason,
                errorMessage:      this.ans.errorMessage,
                errorMessageOther: this.ans.errorMessageOther,
                whichPart:         this.ans.whichPart,
                profileId:         this.ans.affectedProfileId ?? this.ans.resultsProfileId,
                testingType:       this.ans.testingType,
                when:              this.ans.when,
                patientsTesting:   this.ans.patientsTesting,
                patientsAffected:  this.ans.patientsAffected,
                name:              this.contact.name,
                //account:           this.contact.account,
                email:             this.contact.email,
                phone:             this.contact.phone,
                severity:          this.severity,
                source:            this.$route.query.source || '',
            };
            this.submitting = true;
            try {
                const res = await fetch(UPLOAD_TICKETS_URL, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                });
                if (!res.ok) throw new Error('Ticket upload failed: HTTP ' + res.status);
                this.submitted = true;
            } catch (err) {
                // Minimal failure UX — surface an alert so the user knows to retry.
                console.error(err);
                alert('We could not submit your ticket right now. Please try again in a moment.');
            } finally {
                this.submitting = false;
            }
        },
        onReset() {
            this.step = 0;
            this.showErrors = false;
            this.submitted = false;
            this.ans = {
                reason: '', errorMessage: '', errorMessageOther: '', whichPart: '',
                affectedProfileId: '', resultsProfileId: '', testingType: '', when: '',
                patientsTesting: '', patientsAffected: '',
            };
            //this.contact = { name: '', account: '', email: '', phone: '' };
            this.contact = { name: '', email: '', phone: '' };
        }
    }
};
</script>

<style scoped>

.page {
    --brand:                #DD6F48;
    --brand-hover:    #c45a36;
    --brand-tint:     rgba(221,111,72,0.08);
    --ink:                    #4d4d4d;
    --ink-strong:     #3a3a3a;
    --ink-soft:         rgba(94,86,105,0.8);
    --ink-faint:        rgba(94,86,105,0.6);
    --bg-app:             #ebebeb;
    --surface:            #fbfbfb;
    --border:             #e6e7e8;
    --input-border: #d8d5cf;
    --error:                #EF444E;
    --success:            #00A887;

    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0 20px 64px;
    background: var(--bg-app);
    font-family: 'Metropolis', system-ui, -apple-system, sans-serif;
    color: var(--ink);
    -webkit-font-smoothing: antialiased;
}
.page * { box-sizing: border-box; }
.page ::v-deep [hidden] { display: none !important; }

@keyframes hrfade { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }

.page a { color: var(--brand); text-decoration: none; }
.page a:hover { color: var(--brand-hover); text-decoration: underline; }
.page input, .page select, .page textarea { font-family: inherit; }
.page input::placeholder, .page textarea::placeholder { color: rgba(94,86,105,0.5); }
.page ::selection { background: rgba(221,111,72,0.18); }

.topbar {
    width: 100%; max-width: 1100px;
    display: flex; align-items: center; justify-content: space-between;
    padding: 22px 4px 0;
}
.brand { display: flex; align-items: center; gap: 11px; }
.brand svg { width: 26px; height: 26px; flex: none; }
.brand-name { font-weight: 600; font-size: 15px; letter-spacing: 0.2px; color: var(--ink); }
.topbar-tag {
    font-size: 11px; font-weight: 600; text-transform: uppercase;
    letter-spacing: 0.8px; color: var(--ink-faint);
}
.footer-note {
    margin-top: 20px; font-size: 11px; color: rgba(94,86,105,0.5); text-align: center;
}

.card {
    width: 100%; max-width: 640px; margin-top: 16px;
    background: var(--surface); border: 1px solid var(--border);
    border-radius: 4px; overflow: hidden;
}
.card-header { padding: 26px 30px 20px; border-bottom: 1px solid var(--border); }
.eyebrow {
    font-size: 11px; font-weight: 600; text-transform: uppercase;
    letter-spacing: 0.9px; color: var(--brand); margin-bottom: 7px;
}
.card-header h1 {
    margin: 0 0 6px; font-size: 21px; font-weight: 600;
    color: var(--ink-strong); letter-spacing: -0.2px;
}
.card-header p {
    margin: 0; font-size: 13.5px; line-height: 1.5;
    color: var(--ink-soft); max-width: 560px;
}
.req { color: var(--error); }

.stepbar { padding: 16px 30px 4px; }
.stepbar-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 9px; }
.step-label {
    font-size: 11px; font-weight: 600; text-transform: uppercase;
    letter-spacing: 0.7px; color: rgba(94,86,105,0.65);
}
.step-counter { font-size: 11px; font-weight: 600; color: rgba(94,86,105,0.5); }
.progress-track { height: 4px; border-radius: 4px; background: var(--border); overflow: hidden; }
.progress-fill { height: 100%; background: var(--brand); border-radius: 4px; transition: width 0.25s ease; width: 0; }

.steps { padding: 20px 30px 8px; }
.step.active { animation: hrfade 0.2s ease; }

.q-title { font-size: 14px; font-weight: 600; color: var(--ink-strong); margin-bottom: 3px; }
.q-hint { font-size: 12.5px; color: rgba(94,86,105,0.7); margin-bottom: 12px; }

.options { display: flex; flex-wrap: wrap; gap: 8px; }
.options.column { flex-direction: column; flex-wrap: nowrap; }

.option {
    display: inline-flex; align-items: center; gap: 9px;
    padding: 9px 13px; min-height: 38px;
    border: 1px solid var(--border); background: var(--surface);
    color: var(--ink); border-radius: 4px; cursor: pointer;
    font-size: 14px; font-weight: 500; font-family: inherit;
    text-align: left; line-height: 1.2;
    transition: background-color 0.15s, border-color 0.15s;
}
.option:hover { border-color: var(--brand); }
.option .radio {
    width: 16px; height: 16px; border-radius: 50%;
    border: 1.5px solid #c9c4bb; flex: none;
    display: flex; align-items: center; justify-content: center;
}
.option .radio::after {
    content: ''; width: 8px; height: 8px; border-radius: 50%;
    background: var(--brand); opacity: 0; transition: opacity 0.12s;
}
.option.selected {
    border-color: var(--brand); background: var(--brand-tint);
    color: var(--brand-hover); font-weight: 600;
}
.option.selected .radio::after { opacity: 1; }

.field-error { font-size: 11.5px; color: var(--error); margin-top: 7px; }

.followup {
    margin-top: 14px; padding: 14px;
    background: #f6f6f5; border: 1px solid var(--border);
    border-radius: 4px; animation: hrfade 0.2s ease;
}
.followup.stack { display: flex; flex-direction: column; gap: 12px; }
.followup-label { font-size: 12.5px; font-weight: 600; color: var(--ink-strong); margin-bottom: 6px; }
.followup-label .optional { font-weight: 400; color: rgba(94,86,105,0.6); }
.followup .hint-small { font-size: 10.5px; color: rgba(94,86,105,0.55); margin-top: 5px; }

.input, .select, .textarea {
    width: 100%; padding: 0 11px; height: 36px;
    background: #fff; border: 1px solid var(--input-border);
    border-radius: 4px; font-size: 14px; color: var(--ink); outline: none;
}
.select { height: 38px; cursor: pointer; }
.textarea { min-height: 52px; height: auto; padding: 9px 11px; resize: vertical; line-height: 1.5; }
.input.invalid, .select.invalid, .textarea.invalid {
    background: rgba(239,68,78,0.08); border-color: var(--error);
}

.contact-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.contact-grid .sub-label {
    font-size: 12px; font-weight: 500; color: rgba(94,86,105,0.87); margin-bottom: 5px;
}

.card-footer {
    padding: 18px 30px 24px; border-top: 1px solid var(--border);
    display: flex; align-items: center; justify-content: space-between; gap: 12px;
}
.btn-back {
    display: inline-flex; align-items: center; height: 36px; padding: 0 16px;
    background: transparent; color: var(--ink);
    border: 1px solid var(--input-border); border-radius: 4px;
    font-family: inherit; font-size: 14px; font-weight: 500; cursor: pointer;
}
.btn-primary {
    display: inline-flex; align-items: center; height: 36px; padding: 0 20px;
    background: var(--brand); color: #fff; border: none; border-radius: 4px;
    font-family: inherit; font-size: 14px; font-weight: 600; cursor: pointer;
    margin-left: auto;
}
.btn-primary:hover { background: var(--brand-hover); }
.btn-primary:disabled { opacity: 0.7; cursor: not-allowed; }

.confirm { padding: 38px 34px 30px; animation: hrfade 0.25s ease; }
.confirm-head { display: flex; align-items: center; gap: 12px; margin: 0 0 4px; }
.confirm-badge {
    width: 40px; height: 40px; border-radius: 8px; flex: none;
    display: flex; align-items: center; justify-content: center; background: var(--success);
}
.confirm-head h2 { margin: 0; font-size: 20px; font-weight: 600; color: var(--ink-strong); letter-spacing: -0.2px; }
.confirm p { margin: 12px 0 0; font-size: 14px; line-height: 1.6; color: var(--ink); max-width: 600px; }
.confirm .btn-secondary {
    display: inline-flex; align-items: center; height: 34px; padding: 0 16px;
    background: transparent; color: var(--brand);
    border: 1px solid rgba(221,111,72,0.5); border-radius: 4px;
    font-family: inherit; font-size: 14px; font-weight: 500; cursor: pointer;
    margin-top: 26px;
}
.confirm .btn-secondary:hover { background: rgba(221,111,72,0.06); }
</style>

<!-- Global font import — the standalone HTML page loaded this at the <head> level;
     the SPA index.html doesn't, so pull it in here so the Metropolis face is
     available whenever this route mounts. -->
<style>
@import url("https://cdn.jsdelivr.net/npm/@xz/fonts@1/serve/metropolis.min.css");
</style>
